var _defineProperty = require("/codebuild/output/src2922/src/s3/01/node_modules/@babel/runtime/helpers/defineProperty.js");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var MonthlyFixedAmortizationLoan = require('./MonthlyFixedAmortizationLoan');
var MonthlyFixedPaymentLoan = require('./MonthlyFixedPaymentLoan');
var RepaymentType = {
  FIXED_PAYMENT: 'annuity',
  FIXED_AMORTIZATION: 'fixed-amortization'
};

/**
 * Compares one loan against a list of loans to calculate the improvement.
 *
 * @param {Object} loan The loan to compare against the list of loans.
 * @param {Array<Object>} loans The list of loans.
 */
var compareLoans = function compareLoans(loan) {
  var loans = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!loan) {
    return {};
  }
  var totalAmount = 0;
  var remainingLoan = null;
  var totalLoansAmount = loans.reduce(function (total, current) {
    return total + (current.amount || 0);
  }, 0);
  var coveredLoan = loan;
  var loanAmount = coveredLoan.amount || 0;

  /*
   * If the desired loan amount is higher than the list of loans to compare
   * against, only include the amount up to the total loans amount in the
   * calculations.
   */
  if (loanAmount > totalLoansAmount) {
    var factor = 1 - (loanAmount - totalLoansAmount) / loanAmount;
    coveredLoan.amount *= factor;
    coveredLoan.totalCost *= factor;
    coveredLoan.monthlyPayment *= factor;
  }

  /*
   * Determine how many and to what degree the loans in the list should be
   * included in the calculations. Only loans up to the desired loan amount
   * should be included.
   */
  var coveredLoans = loans.map(function (currentLoan) {
    var currentAmount = currentLoan.amount || 0;
    totalAmount += currentAmount || 0;
    /* eslint no-else-return: "off" */
    if (totalAmount <= loanAmount) {
      return currentLoan;
    } else if (totalAmount - currentAmount < loanAmount) {
      var _factor = 1 - (totalAmount - loanAmount) / currentAmount;

      /*
       * Calculate the remainder of the loan as a new loan. If it's a fixed-
       * amortization loan, start with the same monthly payment but calculate a
       * new repayment time. If it's a fixed-payment loan, use the same
       * repayment time but calculate the new monthly payment.
       */
      remainingLoan = {
        amount: totalAmount - loanAmount,
        arrangementFee: currentLoan.arrangementFee,
        administrationFee: currentLoan.administrationFee,
        interest: currentLoan.interest
      };
      if (currentLoan.repaymentType === RepaymentType.FIXED_AMORTIZATION) {
        remainingLoan.monthlyPayment = currentLoan.monthlyPayment;
        var calculatedLoan = new MonthlyFixedAmortizationLoan(remainingLoan);
        remainingLoan.apr = calculatedLoan.getAPR();
        remainingLoan.repaymentTime = calculatedLoan.getRepaymentTime();
        remainingLoan.totalCost = calculatedLoan.getTotalCost();
      } else if (currentLoan.repaymentType === RepaymentType.FIXED_PAYMENT) {
        remainingLoan.repaymentTime = currentLoan.repaymentTime;
        var _calculatedLoan = new MonthlyFixedPaymentLoan(remainingLoan);
        remainingLoan.apr = _calculatedLoan.getAPR();
        remainingLoan.monthlyPayment = _calculatedLoan.getMonthlyPayment();
        remainingLoan.totalCost = _calculatedLoan.getTotalCost();
      }
      return _objectSpread(_objectSpread({}, currentLoan), {}, {
        amount: currentAmount * _factor,
        totalCost: (currentLoan.totalCost || 0) * _factor,
        monthlyPayment: (currentLoan.monthlyPayment || 0) * _factor
      });
    } else {
      return null;
    }
  }).filter(function (current) {
    return !!current;
  });

  /*
   * Calculate the summary of the loan comparison. Percentage values like
   * interest and apr are weighted against the amount of the loan. Negative
   * summary values indicate an improvement.
   */
  return _objectSpread(_objectSpread({}, coveredLoans.reduce(function (summary, currentLoan) {
    return {
      amount: summary.amount - (currentLoan.amount || 0),
      totalCost: summary.totalCost - (currentLoan.totalCost || 0),
      monthlyPayment: summary.monthlyPayment - (currentLoan.monthlyPayment || 0),
      arrangementFee: summary.arrangementFee - (currentLoan.arrangementFee || 0),
      administrationFee: summary.administrationFee - (currentLoan.administrationFee || 0),
      interest: summary.interest - ((currentLoan.interest || 0) * (currentLoan.amount || 0) / loan.amount || 0),
      apr: summary.apr - ((currentLoan.apr || 0) * (currentLoan.amount || 0) / loan.amount || 0)
    };
  }, {
    amount: coveredLoan.amount || 0,
    totalCost: coveredLoan.totalCost || 0,
    monthlyPayment: coveredLoan.monthlyPayment || 0,
    arrangementFee: coveredLoan.arrangementFee || 0,
    administrationFee: coveredLoan.administrationFee || 0,
    interest: coveredLoan.interest || 0,
    apr: coveredLoan.apr || 0
  })), {}, {
    remainingLoan: remainingLoan,
    coveredLoans: coveredLoans.length,
    fullyCoveredLoans: coveredLoans.length - ((remainingLoan || {}).amount > 0 ? 1 : 0)
  });
};
var calculateLoansSummary = function calculateLoansSummary(loans) {
  var amount = loans.reduce(function (total, loan) {
    return total + (loan.amount || 0);
  }, 0);
  var totalCost = loans.reduce(function (total, loan) {
    return total + (loan.totalCost || 0);
  }, 0);
  var monthlyPayment = loans.reduce(function (total, loan) {
    return total + (loan.monthlyPayment || 0);
  }, 0);
  var interest = loans.reduce(function (average, loan) {
    var loanRateFactor = (loan.amount || 0) / amount;
    return average + loanRateFactor * (loan.interest || 0);
  }, 0) || 0;
  var apr = loans.reduce(function (average, loan) {
    var loanRateFactor = (loan.amount || 0) / amount;
    return average + loanRateFactor * (loan.apr || 0);
  }, 0) || 0;
  var administrationFee = loans.reduce(function (total, loan) {
    return total + (loan.administrationFee || 0);
  }, 0);
  var arrangementFee = loans.reduce(function (total, loan) {
    return total + (loan.arrangementFee || 0);
  }, 0);
  return {
    amount: amount,
    totalCost: totalCost,
    monthlyPayment: monthlyPayment,
    interest: interest,
    apr: apr,
    administrationFee: administrationFee,
    arrangementFee: arrangementFee
  };
};
module.exports = {
  compareLoans: compareLoans,
  calculateLoansSummary: calculateLoansSummary
};